<script setup>
import { computed, getCurrentInstance, ref, watch, reactive } from 'vue'
import { router, useForm } from '@inertiajs/vue3';
import MainLayout from '@/layout/main/MainLayout.vue';
import { useLoading } from '@/composables/useLoading';
import { useReviewHistory } from '@/composables/useReviewHistory';
import { useCurrencyFormatter } from '@/utils/currencyFormatter.js';
import { useOrderStatus } from '@/composables/useOrderStatus.js';
import { useToast } from 'primevue/usetoast';
import RadioButton from 'primevue/radiobutton';
import Dialog from 'primevue/dialog';
import axios from 'axios';
import SiteFeaturesInfo from '@/components/SiteFeaturesInfo.vue';
import WiseTransferInstructions from '@/components/payment/WiseTransferInstructions.vue';
import PixInstructions from '@/components/payment/PixInstructions.vue';
import BoletoInstructions from '@/components/payment/BoletoInstructions.vue';
import ClientContentApprovalModal from '@/components/client/orders/ClientContentApprovalModal.vue';
import ClientRejectContentModal from '@/components/client/orders/ClientRejectContentModal.vue';
import ReviewHistoryDialog from '@/components/shared/ReviewHistoryDialog.vue';

defineOptions({
    layout: MainLayout,
});

const { proxy } = getCurrentInstance();
const $t = proxy.$t;
const { showLoading, hideLoading } = useLoading();
const { formatWithSymbol } = useCurrencyFormatter();
const { getStatusLabel: _getStatusLabel, getStatusSeverity, getCancellationTooltip } = useOrderStatus();
const getStatusLabel = (status, $t) => {
    if (status === 'AWAITING_CLIENT_APPROVAL') return $t('Awaiting approval');
    if (status === 'SUBMITTED') return $t('Awaiting Publication');
    return _getStatusLabel(status, $t);
};
const toast = useToast();
const showWiseModal = ref(false);
const showMpModal = ref(false);

const formatDate = (date) => {
    if (!date) return '-';
    if (typeof date === 'string') {
        if (date.includes('T') || date.includes(' ')) {
            return new Date(date).toLocaleDateString('pt-BR');
        }
        const [year, month, day] = date.split('-');
        return new Date(year, month - 1, day).toLocaleDateString('pt-BR');
    }
    return new Date(date).toLocaleDateString('pt-BR');
};

const props = defineProps({
    order: Object,
    coins: Object,
    statusSteps: Object,
    stepCompletion: Object,
    stepConfig: Array,
    activeStepIndex: Number,
    languages: Array,
    wiseBankDetails: {
        type: Object,
        default: null,
    },
    mpPaymentDetails: {
        type: Object,
        default: null,
    },
});

const formattedLanguages = computed(() => {
    if (!props.languages) return [];

    return props.languages
        .map(lang => ({
            id: lang.id,
            name: lang.name,
            code: lang.code
        }))
        .sort((a, b) => a.name.localeCompare(b.name));
});

const orderItems = computed(() => {
    return props.order.order_items.map((orderItem) => {
        return {
            id: orderItem.id,
            url: orderItem.site?.url,
            link: orderItem.link,
            sale: orderItem.sale,
            sale_coin: orderItem.sale_coin,
            payment_tax: orderItem.payment_tax || 0,
            payment_tax_coin: orderItem.payment_tax_coin || orderItem.sale_coin,
            link_status: orderItem.link_status,
            type_name: orderItem.type?.name || null,
            observations: orderItem.observations,
            content: orderItem.content || null,
            language_code: orderItem.site?.language?.code || 'en',
            is_content_confirmed: orderItem.content?.confirmed_by_admin_at != null,
            is_content_rejected: orderItem.content?.rejected_at != null,
            is_content_revised: orderItem.content?.revised_at != null,
            rejection_reason: orderItem.content?.rejection_reason || null,
            has_content: orderItem.content != null,
            sent_to_portal_at: orderItem.content?.sent_to_portal_at || null,
            live_link_confirmed_at: orderItem.live_link_confirmed_at || null,
            pivot_updated_at: orderItem.updated_at || null,
            accepted_anchors: orderItem.accepted_anchors || 1,
            auto_content: orderItem.content?.auto_content ?? null,
            content_id: orderItem.content?.id ?? null,
            sent_to_client_approval_at: orderItem.content?.sent_to_client_approval_at || null,
            client_approved_at: orderItem.content?.client_approved_at || null,
            client_rejected_at: orderItem.content?.client_rejected_at || null,
            client_rejection_reason: orderItem.content?.client_rejection_reason || null,
            content_link: orderItem.content?.content_link || null,
            review_logs: orderItem.content?.review_logs ?? [],
            canceled: !!orderItem.canceled_at,
            canceled_at: orderItem.canceled_at,
            cancellation_reason: orderItem.cancellation_reason,
        };
    });
});

const activeOrderItems = computed(() => orderItems.value.filter(i => !i.canceled));

const changeLogs = computed(() => props.order.change_logs || []);

const showContentModal = ref(false);
const showViewContentModal = ref(false);
const selectedOrderItem = ref(null);
const contentFile = ref(null);

const { showHistoryDialog, historyItem, openHistoryDialog, closeHistoryDialog } = useReviewHistory();

const showRejectApprovalModal = ref(false);
const rejectingContentId = ref(null);
const rejectionReason = ref('');
const approvingContentId = ref(null);
const approvalProcessing = ref(false);

const showContentApprovalModal = ref(false);
const contentApprovalItem = ref(null);

const openContentApprovalModal = (item) => {
    contentApprovalItem.value = item;
    showContentApprovalModal.value = true;
};

const closeContentApprovalModal = () => {
    showContentApprovalModal.value = false;
    contentApprovalItem.value = null;
};

const openRejectFromApprovalModal = (contentId) => {
    closeContentApprovalModal();
    openRejectApprovalModal(contentId);
};

const approveContent = (contentId) => {
    approvingContentId.value = contentId;
    approvalProcessing.value = true;
    router.post(
        proxy.route('client.order-content.client-approve', contentId),
        {},
        {
            preserveScroll: true,
            onStart: () => showLoading($t('Approving content...')),
            onFinish: () => { hideLoading(); approvalProcessing.value = false; approvingContentId.value = null; },
            onSuccess: () => {
                closeContentApprovalModal();
                toast.add({ severity: 'success', summary: $t('Success'), detail: $t('Content approved successfully'), life: 3000 });
            },
            onError: (errors) => {
                const msg = errors?.error || errors?.message || $t('Failed to approve content');
                toast.add({ severity: 'error', summary: $t('Error'), detail: msg, life: 5000 });
            },
        }
    );
};

const openRejectApprovalModal = (contentId) => {
    rejectingContentId.value = contentId;
    rejectionReason.value = '';
    showRejectApprovalModal.value = true;
};

const closeRejectApprovalModal = () => {
    showRejectApprovalModal.value = false;
    rejectingContentId.value = null;
    rejectionReason.value = '';
};

const submitRejectApproval = () => {
    if (!rejectionReason.value.trim()) {
        toast.add({ severity: 'warn', summary: $t('Warning'), detail: $t('Please provide a rejection reason'), life: 3000 });
        return;
    }
    approvalProcessing.value = true;
    router.post(
        proxy.route('client.order-content.client-reject', rejectingContentId.value),
        { rejection_reason: rejectionReason.value },
        {
            preserveScroll: true,
            onStart: () => showLoading($t('Submitting rejection...')),
            onFinish: () => { hideLoading(); approvalProcessing.value = false; },
            onSuccess: () => {
                toast.add({ severity: 'info', summary: $t('Rejection Submitted'), detail: $t('Content rejected. The admin will be notified to review the document.'), life: 5000 });
                closeRejectApprovalModal();
            },
            onError: (errors) => {
                const msg = errors?.rejection_reason?.[0] || errors?.error || $t('Failed to submit rejection');
                toast.add({ severity: 'error', summary: $t('Error'), detail: msg, life: 5000 });
            },
        }
    );
};
const formErrors = ref({});
const contentType = ref(null); 
const siteFeatures = ref(null);
const validatingGoogleDocs = ref(false);
const googleDocsValidationError = ref('');
const visibleAnchors = ref(1); 

const contentForm = reactive({
    anchors: [{ name: '', url: '' }],
    language: '',
    content_link: '',
    auto_content: null,
    message: '',
    reset: function() {
        this.anchors = [{ name: '', url: '' }];
        this.language = '';
        this.content_link = '';
        this.auto_content = null;
        this.message = '';
    }
});

const openContentModal = (item) => {
    selectedOrderItem.value = item;

    const originalOrderItem = props.order.order_items.find(orderItem => orderItem.id === item.id);
    const originalSite = originalOrderItem?.site;
    
    if (originalSite) {
        siteFeatures.value = {
            menu: originalSite.menu,
            banner: originalSite.banner,
            promo: originalSite.promo,
            google_news: originalSite.google_news,
            embed: originalSite.embed,
            restrict: originalSite.restrict,
            archive_article: originalSite.archive_article,

            restrict_detail: originalSite.restrict ? originalSite.restrict_detail : null,
            archive_due: originalSite.archive_article ? originalSite.archive_due : null,
            
            images: originalSite.images,
        };
    } else {
        siteFeatures.value = null;
    }

    const defaultLanguage = formattedLanguages.value.find(
        lang => lang.code === item.language_code
    );
    const defaultLanguageName = defaultLanguage?.name || formattedLanguages.value[0]?.name || '';

    let acceptedAnchors = 1; 
    
    if (item.accepted_anchors && item.accepted_anchors > 0) {
        acceptedAnchors = item.accepted_anchors;
    }
    else if (originalSite?.pivot?.accepted_anchors && originalSite.pivot.accepted_anchors > 0) {
        acceptedAnchors = originalSite.pivot.accepted_anchors;
    }
    else if (originalSite?.pivot?.type?.accepted_anchors && originalSite.pivot.type.accepted_anchors > 0) {
        acceptedAnchors = originalSite.pivot.type.accepted_anchors;
    }
    acceptedAnchors = Math.max(acceptedAnchors, 1);
    
    if (item.content) {
        let existingAnchors = Array(acceptedAnchors).fill().map(() => ({ name: '', url: '' }));
        
        if (item.content.anchor_name || item.content.anchor_url) {
            existingAnchors[0] = {
                name: item.content.anchor_name || '',
                url: item.content.anchor_url || ''
            };
        }
        
        if (item.content.anchor_name_2 || item.content.anchor_url_2) {
            existingAnchors[1] = {
                name: item.content.anchor_name_2 || '',
                url: item.content.anchor_url_2 || ''
            };
        }
        
        if (item.content.anchor_name_3 || item.content.anchor_url_3) {
            existingAnchors[2] = {
                name: item.content.anchor_name_3 || '',
                url: item.content.anchor_url_3 || ''
            };
        }
        
        contentForm.anchors = existingAnchors;
        contentForm.language = item.content.language || defaultLanguageName;
        contentForm.content_link = item.content.content_link || '';
        contentForm.auto_content = item.content.auto_content ?? 0;
        if (item.content.auto_content === 0) {
            contentType.value = 'link';
        } else if (item.content.auto_content === 1) {
            contentType.value = 'article';
        } else {
            contentType.value = null;
        }
    } else {
        contentForm.reset();
        contentForm.anchors = Array(acceptedAnchors).fill().map(() => ({ name: '', url: '' }));
        contentForm.language = defaultLanguageName;
        contentType.value = null; 
    }

    if (item.content) {
        let filledAnchors = 0;
        if (item.content.anchor_name || item.content.anchor_url) filledAnchors++;
        if (item.content.anchor_name_2 || item.content.anchor_url_2) filledAnchors++;
        if (item.content.anchor_name_3 || item.content.anchor_url_3) filledAnchors++;
        visibleAnchors.value = Math.max(1, filledAnchors);
    } else {
        visibleAnchors.value = 1;
    }

    showContentModal.value = true;
};

const closeContentModal = () => {
    showContentModal.value = false;
    selectedOrderItem.value = null;
    siteFeatures.value = null;
    contentForm.reset();
    contentType.value = null; 
    formErrors.value = {};
    googleDocsValidationError.value = '';
    validatingGoogleDocs.value = false;
    visibleAnchors.value = 1;
};

const openViewContentModal = (orderItem) => {
    selectedOrderItem.value = orderItem;
    
    const originalOrderItem = props.order.order_items.find(oi => oi.id === orderItem.id);
    const originalSite = originalOrderItem?.site;
    
    if (originalSite) {
        siteFeatures.value = {
            menu: originalSite.menu,
            banner: originalSite.banner,
            promo: originalSite.promo,
            google_news: originalSite.google_news,
            embed: originalSite.embed,
            restrict: originalSite.restrict,
            archive_article: originalSite.archive_article,
            restrict_detail: originalSite.restrict ? originalSite.restrict_detail : null,
            archive_due: originalSite.archive_article ? originalSite.archive_due : null,
            images: originalSite.images,
        };
    } else {
        siteFeatures.value = null;
    }
    
    showViewContentModal.value = true;
};

const closeViewContentModal = () => {
    showViewContentModal.value = false;
    selectedOrderItem.value = null;
    siteFeatures.value = null;
};

const addAnchor = (targetAnchor = null) => {
    const maxAnchors = selectedOrderItem.value?.accepted_anchors || 1;
    if (targetAnchor) {
        visibleAnchors.value = Math.min(targetAnchor, maxAnchors);
    } else {
        if (visibleAnchors.value < maxAnchors) {
            visibleAnchors.value++;
        }
    }
};

const removeAnchor = (index) => {
    if (visibleAnchors.value > 1 && index === visibleAnchors.value - 1) {
        contentForm.anchors[index].name = '';
        contentForm.anchors[index].url = '';
        visibleAnchors.value--;
    }
};

watch(() => contentForm.auto_content, (newValue) => {
    if (newValue === 1) {
        contentForm.content_link = '';
    }
});
const onContentTypeChange = () => {
    if (contentType.value === 'link') {
        contentForm.auto_content = 0;
    } else if (contentType.value === 'article') {
        contentForm.auto_content = 1;
        contentForm.content_link = '';
    } else {
        contentForm.auto_content = null;
    }
};

const toggleContentType = (value) => {
    if (contentType.value === value) {
        contentType.value = null;
        contentForm.auto_content = null;
        contentForm.content_link = '';
        googleDocsValidationError.value = '';
    } else {
        contentType.value = value;
        onContentTypeChange();
        googleDocsValidationError.value = '';
    }
};

const validateGoogleDocsContent = async () => {
    if (!contentForm.content_link) {
        return true; 
    }
    
    try {
        validatingGoogleDocs.value = true;
        googleDocsValidationError.value = '';
        
        const response = await axios.post('/google-docs/validate-content', {
            google_docs_url: contentForm.content_link,
            anchor_name: contentForm.anchors[0]?.name?.trim() || null,
            anchor_url: contentForm.anchors[0]?.url?.trim() || null,
            anchor_name_2: contentForm.anchors[1]?.name?.trim() || null,
            anchor_url_2: contentForm.anchors[1]?.url?.trim() || null,
            anchor_name_3: contentForm.anchors[2]?.name?.trim() || null,
            anchor_url_3: contentForm.anchors[2]?.url?.trim() || null,
        });
        
        if (response.data.valid === true) {
            return true;
        } else if (response.data.valid === 'similar') {
            similarityData.value = {
                similarity: response.data.similarity,
                expected: response.data.expected,
                found: response.data.found,
                url: response.data.url,
                position: response.data.position || 1
            };
            showSimilarityConfirmation.value = true;
            return false;
        } else {
            const errors = response.data.errors || [];
            if (errors.length > 0) {
                googleDocsValidationError.value = errors.join('<br>');
            } else {
                googleDocsValidationError.value = response.data.error || $t('Unknown validation error');
            }
            throw new Error('validation_failed');
        }
    } catch (error) {
        if (error.message === 'validation_failed') {
            throw error;
        } else {
            if (error.response) {
                const status = error.response.status;
                const data = error.response.data;
                
                if (status === 404) {
                    googleDocsValidationError.value = $t('Validation service not found. Please contact support.');
                } else if (status === 422) {
                    googleDocsValidationError.value = data.message || $t('Invalid request data.');
                } else if (status === 500) {
                    googleDocsValidationError.value = data.error || $t('Server error occurred during validation.');
                } else {
                    googleDocsValidationError.value = `${$t('Error')} ${status}: ${data.error || data.message || $t('Unknown error')}`;
                }
            } else if (error.request) {
                googleDocsValidationError.value = $t('No response from validation service. Please check your internet connection.');
            } else {
                googleDocsValidationError.value = $t('Error setting up validation request. Please try again.');
            }
            throw error;
        }
    } finally {
        validatingGoogleDocs.value = false;
    }
};

watch(() => contentForm.content_link, () => {
    googleDocsValidationError.value = '';
});
 
watch(() => contentForm.anchors, () => {
    googleDocsValidationError.value = '';
}, { deep: true });


const submitting = ref(false);
const showSimilarityConfirmation = ref(false);
const similarityData = ref(null);

const submitContent = async (forceContinue = false) => {
    if (forceContinue && typeof forceContinue === 'object' && forceContinue.type) {
        forceContinue = false; 
    }
    
    if (!selectedOrderItem.value) return;

    const hasValidAnchor = contentForm.anchors.some(anchor => anchor.name.trim() && anchor.url.trim());
    const hasIncompleteAnchor = contentForm.anchors.some(anchor => {
        const hasName = anchor.name.trim().length > 0;
        const hasUrl = anchor.url.trim().length > 0;
        return (hasName && !hasUrl) || (!hasName && hasUrl);
    });
    
    if (!hasValidAnchor || !contentForm.language) {
        toast.add({
            severity: 'warn',
            summary: $t('Validation Error'),
            detail: $t('Please fill in at least one anchor name and URL, and select a language'),
            life: 3000
        });
        return;
    }
    
    if (hasIncompleteAnchor) {
        toast.add({
            severity: 'error',
            summary: $t('Incomplete Anchors'),
            detail: $t('Anchor fields must be either completely filled (both name and URL) or completely empty. Please complete the incomplete anchors or clear them.'),
            life: 5000
        });
        return;
    }

    if (!contentType.value) {
        toast.add({
            severity: 'warn',
            summary: $t('Validation Error'),
            detail: $t('Please select a content type'),
            life: 3000
        });
        return;
    }
    
    if (contentType.value === 'link' && !contentForm.content_link) {
        toast.add({
            severity: 'warn',
            summary: $t('Validation Error'),
            detail: $t('Please provide a Google Docs link'),
            life: 3000
        });
        return;
    }

    if (contentType.value === 'link' && contentForm.content_link && !forceContinue) {
        submitting.value = true
        
        try {
            const validationResult = await validateGoogleDocsContent();

            if (validationResult === false) {
                submitting.value = false 
                return;
            }

            if (googleDocsValidationError.value) {
                submitting.value = false;
                return;
            }

        } catch (error) {
            submitting.value = false;
            toast.add({
                severity: 'error',
                summary: $t('Validation Error'),
                detail: $t('Error occurred while validating Google Docs content'),
                life: 3000
            });
            return;
        }
    }

    if (!submitting.value) {
        submitting.value = true;
    }

    const formData = {
        anchor_name: contentForm.anchors[0]?.name?.trim() || null,
        anchor_url: contentForm.anchors[0]?.url?.trim() || null,
        anchor_name_2: contentForm.anchors[1]?.name?.trim() || null,
        anchor_url_2: contentForm.anchors[1]?.url?.trim() || null,
        anchor_name_3: contentForm.anchors[2]?.name?.trim() || null,
        anchor_url_3: contentForm.anchors[2]?.url?.trim() || null,
        language: contentForm.language,
        content_link: contentForm.content_link,
        auto_content: contentForm.auto_content,
        message: contentForm.message?.trim() || null,
    };
    
    if (forceContinue) {
        formData.force_continue = true;
    }

    router.post(
        proxy.route('client.order-items.content.store', selectedOrderItem.value.id),
        formData,
        {
            forceFormData: true,
            onSuccess: () => {
                toast.add({
                    severity: 'success',
                    summary: $t('Success'),
                    detail: $t('Content submitted successfully'),
                    life: 3000
                });
                closeContentModal();
                submitting.value = false;
            },
            onError: (errors) => {
                console.error('Errors:', errors);
                formErrors.value = errors;

                const firstError = Object.values(errors)[0];
                const errorMessage = Array.isArray(firstError) ? firstError[0] : firstError || $t('Failed to submit content');

                toast.add({
                    severity: 'error',
                    summary: $t('Error'),
                    detail: errorMessage,
                    life: 5000
                });
                submitting.value = false;
            },
            onFinish: () => {
                submitting.value = false;
            }
        }
    );
};

const confirmSimilarity = () => {
    showSimilarityConfirmation.value = false;
    similarityData.value = null;
    submitContent(true); 
};

const cancelSimilarity = () => {
    showSimilarityConfirmation.value = false;
    similarityData.value = null;
    submitting.value = false;
};

const highlightDifferences = (text1, text2) => {
    const words1 = text1.split(' ');
    const words2 = text2.split(' ');
    const maxLength = Math.max(words1.length, words2.length);
    
    let highlighted1 = '';
    let highlighted2 = '';
    
    for (let i = 0; i < maxLength; i++) {
        const word1 = words1[i] || '';
        const word2 = words2[i] || '';
        
        if (word1 !== word2) {
            highlighted1 += `<span class="bg-red-200 text-red-800 px-1 rounded">${word1}</span> `;
            highlighted2 += `<span class="bg-green-200 text-green-800 px-1 rounded">${word2}</span> `;
        } else {
            highlighted1 += word1 + ' ';
            highlighted2 += word2 + ' ';
        }
    }
    
    return {
        highlighted1: highlighted1.trim(),
        highlighted2: highlighted2.trim()
    };
};

const orderTotalsByCurrency = computed(() => {
    const totals = {};

    activeOrderItems.value.forEach(item => {
        const currency = item.sale_coin;
        if (!totals[currency]) {
            totals[currency] = 0;
        }
        const itemPrice = item.sale / 100;
        const paymentTax = (item.payment_tax || 0) / 100;
        totals[currency] += itemPrice + paymentTax;
    });

    return totals;
});

const formattedTotal = computed(() => {
    const currencies = Object.keys(orderTotalsByCurrency.value);

    if (currencies.length === 0) {
        return formatWithSymbol(0, 'USD');
    }

    if (currencies.length === 1) {
        const currency = currencies[0];
        const total = orderTotalsByCurrency.value[currency];
        return formatWithSymbol(total, currency);
    }

    return currencies
        .map(currency => formatWithSymbol(orderTotalsByCurrency.value[currency], currency))
        .join(' + ');
});

const orderSubtotalsByCurrency = computed(() => {
    const subtotals = {};

    activeOrderItems.value.forEach(item => {
        const currency = item.sale_coin;
        if (!subtotals[currency]) {
            subtotals[currency] = 0;
        }
        subtotals[currency] += item.sale / 100;
    });

    return subtotals;
});

const orderPaymentTaxByCurrency = computed(() => {
    const taxes = {};

    activeOrderItems.value.forEach(item => {
        const currency = item.sale_coin;
        if (!taxes[currency]) {
            taxes[currency] = 0;
        }
        taxes[currency] += (item.payment_tax || 0) / 100;
    });

    return taxes;
});

const formatItemPrice = (price, currency) => {
    const priceInUnits = price / 100;
    return formatWithSymbol(priceInUnits, currency);
}

const formatPaymentTax = (paymentTax, currency) => {
    const taxInUnits = (paymentTax || 0) / 100;
    return formatWithSymbol(taxInUnits, currency);
}

const orderStepsConfig = computed(() => {
    const stepConfig = props.stepConfig || [];
    const stepLabels = {
        'ORDER_RECEIVED': $t('Order Received'),
        'WAITING': $t('Awaiting Payment Confirmation'),
        'PRODUCTION': $t('Content in Review'),
        'AWAITING_CLIENT_APPROVAL': $t('Awaiting approval'),
        'SUBMITTED': $t('Sent to Portal'),
        'PROCESSING': $t('Awaiting Publication'), 
        'PUBLISHED': $t('Published'),
        'INVOICE': $t('Invoice'),
        'COMPLETED': $t('Completed')
    };
    
    const stepIcons = {
        'ORDER_RECEIVED': 'pi pi-shopping-cart',
        'WAITING': 'pi pi-dollar',
        'PRODUCTION': 'pi pi-file-edit',
        'AWAITING_CLIENT_APPROVAL': 'pi pi-file-word',
        'SUBMITTED': 'pi pi-send',
        'PROCESSING': 'pi pi-clock',
        'PUBLISHED': 'pi pi-check-circle',
        'INVOICE': 'pi pi-file',
        'COMPLETED': 'pi pi-flag'
    };
    
    return stepConfig.map(stepKey => ({
        key: stepKey,
        icon: stepIcons[stepKey] || 'pi pi-circle',
        label: stepLabels[stepKey] || stepKey,
        isCompleted: stepKey === 'COMPLETED'
    }));
});

const activeStepIndex = computed(() => {
    const base = props.activeStepIndex || 0;
    if (props.order.status === 'SUBMITTED') return base + 1;
    return base;
});

const getStepDatesFromMostDelayedItem = computed(() => {
    const dates = props.statusSteps || {};
    
    return {
        ORDER_RECEIVED: dates.ORDER_RECEIVED || null,
        WAITING: dates.WAITING || null,
        PRODUCTION: dates.PRODUCTION || null,
        AWAITING_CLIENT_APPROVAL: dates.AWAITING_CLIENT_APPROVAL || null,
        SUBMITTED: dates.SUBMITTED || null,
        PROCESSING: dates.PROCESSING || null,
        PUBLISHED: dates.PUBLISHED || null,
        INVOICE: dates.INVOICE || null,
        COMPLETED: dates.COMPLETED || null,
        CANCELLED: dates.CANCELLED || null,
    };
});


const steps = computed(() => {
    const stepDates = getStepDatesFromMostDelayedItem.value;
    let stepsArray = [];
    
    orderStepsConfig.value.forEach((config) => {
        const date = stepDates[config.key];
        stepsArray.push({
            label: config.label,
            icon: config.icon,
            date: date ? formatDate(date) : null,
            isCompleted: config.isCompleted || false,
            key: config.key
        });
    });
    
    if (props.order.status === 'CANCELLED' || props.order.status === 'CANCELED') {
        const cancelledStep = {
            label: $t('Canceled'),
            icon: 'pi pi-times-circle',
            date: stepDates.CANCELLED ? formatDate(stepDates.CANCELLED) : null,
            isCompleted: false,
            isCancelled: true,
            key: 'CANCELLED'
        };
        
        const activeIndex = getActiveStepIndexForCancelled();
        stepsArray.splice(activeIndex + 1, 0, cancelledStep);
    }
    
    return stepsArray;
});

const getActiveStepIndexForCancelled = () => {
    const stepCompletion = getStepCompletionStatus.value;
    const configSteps = orderStepsConfig.value;

    for (let i = configSteps.length - 1; i >= 0; i--) {
        const stepKey = configSteps[i].key;
        if (stepCompletion[stepKey]?.completed) {
            return i;
        }
    }
    
    return 0; 
};


const getStepCompletionStatus = computed(() => {
    const completion = props.stepCompletion || {};
    
    const defaultStepStatus = { completed: false, partial: false };
    
    return {
        ORDER_RECEIVED: completion.ORDER_RECEIVED || defaultStepStatus,
        WAITING: completion.WAITING || defaultStepStatus,
        PRODUCTION: completion.PRODUCTION || defaultStepStatus,
        AWAITING_CLIENT_APPROVAL: completion.AWAITING_CLIENT_APPROVAL || defaultStepStatus,
        SUBMITTED: completion.SUBMITTED || defaultStepStatus,
        PROCESSING: completion.PROCESSING || defaultStepStatus,
        PUBLISHED: completion.PUBLISHED || defaultStepStatus,
        INVOICE: completion.INVOICE || defaultStepStatus,
        COMPLETED: completion.COMPLETED || defaultStepStatus,
        CANCELLED: completion.CANCELLED || defaultStepStatus,
    };
});

const getStepClasses = (index) => {
    const step = steps.value[index];
    if (!step) return { pending: true };
    
    const stepKey = step.key;
    const currentOrderStatus = props.order.status;
    const currentStepIndex = activeStepIndex.value;
    const stepCompletion = getStepCompletionStatus.value;
    
    if (step.isCancelled) {
        return {
            completed: false,
            partial: false,
            active: false,
            pending: false,
            cancelled: true,
        };
    }
    
    if (currentOrderStatus === 'CANCELLED' || currentOrderStatus === 'CANCELED') {
        if (index > currentStepIndex) {
            return {
                completed: false,
                partial: false,
                active: false,
                pending: true,
            };
        }
        
        const isStepCompleted = stepCompletion[stepKey]?.completed === true;
        const isStepPartial = stepCompletion[stepKey]?.partial === true;
        
        if (isStepCompleted) {
            return {
                completed: true,
                partial: false,
                active: false,
                pending: false,
            };
        } else if (isStepPartial) {
            return {
                completed: false,
                partial: true,
                active: false,
                pending: false,
            };
        } else {
            return {
                completed: false,
                partial: false,
                active: false,
                pending: true,
            };
        }
    }

    if (stepKey === 'COMPLETED' && currentOrderStatus === 'COMPLETED') {
        return {
            completed: true,
            partial: false,
            active: false,
            pending: false,
        };
    }

    if (stepKey === 'WAITING' && currentOrderStatus === 'COMPLETED' && props.order.deferred_payment == 1) {
        return {
            completed: true,
            partial: false,
            active: false,
            pending: false,
        };
    }

    if (index < currentStepIndex) {
        return {
            completed: true,
            partial: false,
            active: false,
            pending: false,
        };
    } else if (index === currentStepIndex) {
        return {
            completed: false,
            partial: false,
            active: true,
            pending: false,
        };
    } else {
        return {
            completed: false,
            partial: false,
            active: false,
            pending: true,
        };
    }
};

const openExternalSite = (url) => {
    try {
        if (!url) return;
        const cleanUrl = url.replace(/^https?:\/\//, '').trim();
        if (!cleanUrl) return;
        const fullUrl = `https://${cleanUrl}`;
        window.open(fullUrl, '_blank', 'noopener,noreferrer');
    } catch (e) {
        console.error('Error opening external site:', e);
    }
}
</script>

<template>

    <div class="grid px-2 sm:px-4 lg:px-0 w-full max-w-full">
        <!-- Simple Header with Back Button -->
        <div class="mb-6 flex items-center justify-between">
            <div>
                <h1 class="text-2xl sm:text-3xl font-bold text-surface-900 dark:text-surface-0">
                    {{ $t('Order') }} #{{ order.number }}
                </h1>
                <p class="text-surface-600 dark:text-surface-400 text-sm mt-1">{{ $t('Track your order status and details') }}</p>
            </div>
            <Button
                :label="$t('Back')"
                icon="pi pi-arrow-left"
                severity="secondary"
                text
                @click="() => {
                    try {
                        const routeName = proxy.route ? proxy.route('client.orders.index') : '/client/orders';
                        router.visit(routeName, {
                            onStart: () => showLoading($t('Loading orders...')),
                            onFinish: () => hideLoading(),
                            onError: () => hideLoading()
                        });
                    } catch (e) { console.error(e); }
                }"
            />
        </div>

        <!-- Beautiful Order Progress -->
        <div class="card mb-6">
            <div class="px-6 py-8 md:px-8">
                <div class="mb-6">
                    <h2 class="text-xl font-bold text-surface-900 dark:text-surface-0">{{ $t('Order Progress') }}</h2>
                    <p class="text-surface-600 dark:text-surface-300 text-sm mt-1">{{ steps[activeStepIndex]?.label }}</p>
                </div>

                <!-- Cancellation details -->
                <div
                    v-if="order.status === 'CANCELLED' || order.status === 'CANCELED'"
                    class="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg"
                >
                    <p class="text-sm font-semibold text-red-700 dark:text-red-300 mb-1">
                        <i class="pi pi-times-circle mr-1"></i>
                        {{ $t('Order canceled') }}<span v-if="order.canceled_at"> - {{ new Date(order.canceled_at).toLocaleDateString() }}</span>
                    </p>
                    <p class="text-sm text-red-900 dark:text-red-200 whitespace-pre-line">
                        {{ order.cancellation_reason || $t('No reason provided') }}
                    </p>
                </div>

                <!-- Custom Horizontal Stepper -->
                <div class="stepper-container">
                    <div class="stepper-list">
                        <div
                            v-for="(step, index) in steps"
                            :key="index"
                            class="stepper-item"
                            :class="getStepClasses(index)"
                        >
                            <div class="stepper-header">
                                <span class="stepper-number">{{ index + 1 }}</span>
                                <div class="stepper-details">
                                    <span class="stepper-title">{{ step.label }}</span>
                                    <span v-if="step.date" class="stepper-date">
                                        <i class="pi pi-check-circle"></i>
                                        {{ step.date }}
                                    </span>
                                    <span v-else class="stepper-date stepper-date-pending">
                                        <i class="pi pi-clock"></i>
                                        {{ $t('Pending') }}
                                    </span>
                                </div>
                            </div>
                            <span
                                v-if="index < steps.length - 1"
                                class="stepper-separator"
                            ></span>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Order Details -->
        <Card class="mb-6">
            <template #header>
                <div class="p-4 sm:p-6 pb-0">
                    <div class="flex items-center justify-between mb-4">
                        <div class="flex items-center gap-3">
                            <i class="pi pi-list text-xl sm:text-2xl text-surface-500" />
                            <div>
                                <h3 class="text-lg font-semibold">{{ $t('Order Details') }}</h3>
                                <span class="text-xs sm:text-sm text-surface-500">{{ orderItems.length }} {{ $t('items') }}</span>
                            </div>
                        </div>
                    </div>

                    <!-- Order Summary Bar -->
                    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-4 p-4 bg-surface-50 dark:bg-surface-800 rounded-lg border border-surface-200 dark:border-surface-700">
                        <!-- Status -->
                        <div class="flex items-center gap-3">
                            <div class="w-12 h-12 rounded-lg bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center flex-shrink-0">
                                <i class="pi pi-info-circle text-primary-600 dark:text-primary-400 text-xl"></i>
                            </div>
                            <div class="min-w-0">
                                <div class="text-xs text-surface-600 dark:text-surface-400 font-medium">{{ $t('Status') }}</div>
                                <div class="flex items-center gap-2 mt-1">
                                    <Tag
                                        :value="(order.status === 'CANCELLED' || order.status === 'CANCELED') ? $t('Canceled') : order.status === 'SUBMITTED' ? $t('Awaiting Publication') : (steps[activeStepIndex]?.label || $t('Unknown Status'))"
                                        :severity="(order.status === 'CANCELLED' || order.status === 'CANCELED') ? 'danger' : (steps[activeStepIndex]?.key ? getStatusSeverity(steps[activeStepIndex].key) : 'secondary')"
                                        v-tooltip.top="getCancellationTooltip(order, $t)"
                                    />
                                    <Button
                                        v-if="order.payment_method === 'wise' && order.status === 'WAITING' && wiseBankDetails"
                                        icon="pi pi-send"
                                        text
                                        rounded
                                        size="small"
                                        severity="info"
                                        v-tooltip.top="$t('Wise transfer details')"
                                        @click="showWiseModal = true"
                                    />
                                    <Button
                                        v-if="order.payment_method === 'pix' && order.status === 'WAITING' && mpPaymentDetails"
                                        icon="pi pi-qrcode"
                                        text
                                        rounded
                                        size="small"
                                        severity="info"
                                        v-tooltip.top="$t('Pix payment details')"
                                        @click="showMpModal = true"
                                    />
                                    <Button
                                        v-if="order.payment_method === 'boleto' && order.status === 'WAITING' && mpPaymentDetails"
                                        icon="pi pi-barcode"
                                        text
                                        rounded
                                        size="small"
                                        severity="secondary"
                                        v-tooltip.top="$t('Boleto payment details')"
                                        @click="showMpModal = true"
                                    />
                                </div>
                            </div>
                        </div>

                        <!-- Created Date -->
                        <div class="flex items-center gap-3">
                            <div class="w-12 h-12 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
                                <i class="pi pi-calendar-plus text-blue-600 dark:text-blue-400 text-xl"></i>
                            </div>
                            <div class="min-w-0">
                                <div class="text-xs text-surface-600 dark:text-surface-400 font-medium">{{ $t('Created') }}</div>
                                <div class="text-sm font-semibold text-surface-900 dark:text-surface-0 truncate">
                                    {{ formatDate(order.created_at) }}
                                </div>
                            </div>
                        </div>

                        <!-- Items Count -->
                        <div class="flex items-center gap-3">
                            <div class="w-12 h-12 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center flex-shrink-0">
                                <i class="pi pi-list text-purple-600 dark:text-purple-400 text-xl"></i>
                            </div>
                            <div class="min-w-0">
                                <div class="text-xs text-surface-600 dark:text-surface-400 font-medium">{{ $t('Total Items') }}</div>
                                <div class="text-lg font-bold text-purple-600 dark:text-purple-400">{{ orderItems.length }}</div>
                            </div>
                        </div>

                        <!-- Total Value -->
                        <div class="flex items-center gap-3">
                            <div class="w-12 h-12 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center flex-shrink-0">
                                <i class="pi pi-dollar text-green-600 dark:text-green-400 text-xl"></i>
                            </div>
                            <div class="min-w-0">
                                <div class="text-xs text-surface-600 dark:text-surface-400 font-medium">{{ $t('Total Value') }}</div>
                                <div class="text-lg font-bold text-green-600 dark:text-green-400 truncate">{{ formattedTotal }}</div>
                            </div>
            </div>
        </div>
    </div>
    </template>
    
    <!-- Similarity Confirmation Modal -->
    <Dialog
        v-model:visible="showSimilarityConfirmation"
        modal
        :closable="false"
        :header="$t('Similarity Confirmation')"
        class="w-11/12 md:w-3/4 lg:w-2/3 xl:w-1/2"
    >
        <div v-if="similarityData" class="space-y-6">
            <div class="grid md:grid-cols-2 gap-4">
                <div class="bg-red-50 border border-red-200 rounded-lg p-4">
                    <h4 class="font-semibold text-red-800 mb-2">{{ $t('Anchor Name (Yours):') }}</h4>
                    <div 
                        class="text-sm text-red-700 break-words"
                        v-html="highlightDifferences(similarityData.expected, similarityData.found).highlighted1"
                    ></div>
                </div>
                
                <div class="bg-green-50 border border-green-200 rounded-lg p-4">
                    <h4 class="font-semibold text-green-800 mb-2">{{ $t('Found in Document:') }}</h4>
                    <div 
                        class="text-sm text-green-700 break-words"
                        v-html="highlightDifferences(similarityData.expected, similarityData.found).highlighted2"
                    ></div>
                </div>
            </div>
            
            <div class="text-center text-sm text-gray-600">
                <p class="text-xs">
                    <strong>{{ $t('Continue Anyway') }}:</strong> {{ $t('Save with the current name (accepts the difference)') }}
                    <br>
                    <strong>{{ $t('Cancel') }}:</strong> {{ $t('Back to editing') }}
                </p>
            </div>
        </div>
        
        <template #footer>
            <div class="flex justify-center space-x-4">
                <Button
                    :label="$t('Cancel')"
                    icon="pi pi-times"
                    class="p-button-secondary"
                    @click="cancelSimilarity"
                />
                <Button
                    :label="$t('Continue Anyway')"
                    icon="pi pi-check"
                    class="p-button-warning"
                    @click="confirmSimilarity"
                />
            </div>
        </template>
    </Dialog>
    </Card>

    <!-- Content Section -->
    <Card class="mb-6">
        <template #header>
            <div class="p-4 sm:p-6 pb-0">
                <h2 class="text-xl font-bold text-surface-900 dark:text-surface-0">{{ $t('Order Items') }}</h2>
                <p class="text-surface-600 dark:text-surface-300 text-sm mt-1">{{ $t('Manage your article content') }}</p>
            </div>
        </template>
        
        <template #content>
            <!-- Mobile: Card View -->
            <div class="block lg:hidden">
                <div v-if="orderItems.length === 0" class="text-center py-8 text-surface-500 italic">
                    {{ $t('No site have been added to this order yet.') }}
                </div>

                <div v-else class="space-y-3">
                    <Card v-for="(item, index) in orderItems" :key="index" class="p-4">
                        <template #content>
                            <div class="flex justify-between items-start mb-3">
                                <div class="flex items-center gap-3 min-w-0 flex-1">
                                    <img
                                        :src="`https://www.google.com/s2/favicons?domain=${item.url}`"
                                        class="w-6 h-6 flex-shrink-0"
                                    />
                                    <div class="min-w-0 flex-1">
                                        <h4 class="text-sm font-medium text-surface-900 truncate" :class="{ 'line-through opacity-60': item.canceled }">{{ item.url }}</h4>
                                        <div v-if="item.link" class="flex items-center gap-2 mt-1">
                                            <span class="text-xs text-surface-500 truncate" :class="{ 'line-through opacity-60': item.canceled }">{{ item.link }}</span>
                                            <Button
                                                icon="pi pi-external-link"
                                                text
                                                size="small"
                                                @click="() => openExternalSite(item.link)"
                                            />
                                        </div>
                                        <div v-else class="text-xs text-surface-500 mt-1">{{ $t('No link yet') }}</div>
                                    </div>
                                </div>
                                <Button
                                    icon="pi pi-external-link"
                                    text
                                    rounded
                                    size="small"
                                    @click="() => openExternalSite(item.url)"
                                />
                            </div>

                            <div class="grid grid-cols-2 gap-3 text-xs">
                                <div>
                                    <span class="font-medium text-surface-700">{{ $t('Type') }}:</span>
                                    <div class="text-surface-900 font-medium">
                                        {{ item.type_name || '-' }}
                                    </div>
                                </div>
                                <div>
                                    <span class="font-medium text-surface-700">{{ $t('Value') }}:</span>
                                    <div class="text-surface-600 font-semibold">
                                        {{ formatItemPrice(item.sale, item.sale_coin) }}
                                    </div>
                                    <div v-if="item.payment_tax && item.payment_tax > 0" class="text-xs text-orange-600">
                                        + {{ formatPaymentTax(item.payment_tax, item.sale_coin) }} {{ $t('tax') }}
                                    </div>
                                </div>
                                <div class="col-span-2">
                                    <span class="font-medium text-surface-700">{{ $t('Status') }}:</span>
                                    <div class="mt-1">
                                        <Tag
                                            v-if="item.canceled"
                                            :value="$t('Canceled')"
                                            severity="danger"
                                            size="small"
                                        />
                                        <Tag
                                            v-else
                                            :value="item.link_status === 'PRODUCTION' && item.is_content_rejected ? $t('Content Rejected') : getStatusLabel(item.link_status || 'PRODUCTION', $t)"
                                            :severity="item.link_status === 'PRODUCTION' && item.is_content_rejected ? 'danger' : getStatusSeverity(item.link_status || 'PRODUCTION')"
                                            size="small"
                                        />
                                    </div>
                                </div>
                            </div>

                            <div v-if="item.observations" class="mt-3 pt-3 border-t border-surface-200">
                                <span class="text-xs font-medium text-surface-700">{{ $t('Observations') }}:</span>
                                <p class="text-xs text-surface-600 mt-1">{{ item.observations }}</p>
                            </div>

                            <!-- Content Rejection Notice (admin rejection) -->
                            <div v-if="item.is_content_rejected" class="mt-3 pt-3 border-t border-surface-200">
                                <div class="p-3 bg-red-50 border border-red-200 rounded-lg">
                                    <div class="flex items-center gap-2 mb-2">
                                        <i class="pi pi-exclamation-triangle text-red-600"></i>
                                        <span class="text-sm font-medium text-red-800">{{ $t('Content Revision Required') }}</span>
                                    </div>
                                    <p class="text-xs text-red-700 mb-2">{{ $t('Your content was reviewed and requires changes. Please revise and resubmit.') }}</p>
                                    <div v-if="item.rejection_reason" class="text-xs text-red-700">
                                        <strong>{{ $t('Feedback') }}:</strong> {{ item.rejection_reason }}
                                    </div>
                                </div>
                            </div>

                            <!-- Client Approval Section (auto_content) -->
                            <div v-if="item.sent_to_client_approval_at" class="mt-3 pt-3 border-t border-surface-200">
                                <!-- Já aprovado pelo cliente -->
                                <div v-if="item.client_approved_at" class="p-3 bg-green-50 border border-green-200 rounded-lg">
                                    <div class="flex items-center gap-2 text-green-700">
                                        <i class="pi pi-check-circle"></i>
                                        <span class="text-sm font-medium">{{ $t('You approved this article') }}</span>
                                    </div>
                                </div>
                                <!-- Rejeitado pelo cliente -->
                                <div v-else-if="item.client_rejected_at" class="p-3 bg-orange-50 border border-orange-200 rounded-lg">
                                    <div class="flex items-center gap-2 text-orange-700 mb-1">
                                        <i class="pi pi-times-circle"></i>
                                        <span class="text-sm font-medium">{{ $t('You rejected this content') }}</span>
                                    </div>
                                    <p v-if="item.client_rejection_reason" class="text-xs text-orange-800">{{ item.client_rejection_reason }}</p>
                                    <p class="text-xs text-orange-600 mt-1">{{ $t('The admin will review and send a new version') }}</p>
                                </div>
                                <!-- Aguardando aprovação -->
                                <div v-else class="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                                    <div class="flex items-center gap-2 mb-2">
                                        <i class="pi pi-clock text-blue-600"></i>
                                        <span class="text-sm font-medium text-blue-800">{{ $t('Awaiting your approval') }}</span>
                                    </div>
                                    <Button
                                        :label="$t('Evaluate Article')"
                                        icon="pi pi-pencil"
                                        severity="info"
                                        size="small"
                                        class="w-full"
                                        @click="openContentApprovalModal(item)"
                                    />
                                </div>
                            </div>

                            <!-- Review History Button -->
                            <div v-if="item.review_logs?.length" class="mt-3 pt-3 border-t border-surface-200">
                                <Button
                                    icon="pi pi-history"
                                    :label="$t('Review History')"
                                    size="small"
                                    severity="secondary"
                                    text
                                    class="w-full"
                                    @click="openHistoryDialog(item)"
                                />
                            </div>

                            <div class="mt-3 pt-3 border-t border-surface-200">
                                <Button
                                    v-if="props.order.status === 'CANCELED' || item.canceled"
                                    :label="$t('Canceled')"
                                    icon="pi pi-ban"
                                    severity="secondary"
                                    size="small"
                                    class="w-full"
                                    disabled
                                />
                                <Button
                                    v-else
                                    :label="item.is_content_rejected ? $t('Revise Content') : (item.content ? $t('Edit Content') : $t('Add Content'))"
                                    :icon="item.is_content_rejected ? 'pi pi-exclamation-triangle' : (item.content ? 'pi pi-pencil' : 'pi pi-plus')"
                                    :severity="item.is_content_rejected ? 'danger' : (item.content ? 'secondary' : 'primary')"
                                    size="small"
                                    class="w-full"
                                    :disabled="item.is_content_confirmed"
                                    @click="openContentModal(item)"
                                />
                            </div>
                        </template>
                    </Card>
                </div>
            </div>

            <!-- Desktop: Table View -->
            <!-- min-w-0 + overflow-x-auto keep the wide items table inside the page
                 (scrolls internally) instead of stretching the layout past the viewport. -->
            <div class="hidden lg:block min-w-0 overflow-x-auto">
                <DataTable
                    :value="orderItems"
                    showGridlines
                    size="small"
                    class="order-items-table"
                    scrollable
                    scrollHeight="600px"
                    responsiveLayout="scroll"
                >
                    <Column field="url" :header="$t('Portal')" style="min-width: 300px; width: 35%">
                        <template #body="slotProps">
                            <div class="flex items-center gap-2">
                                <img
                                    :src="`https://www.google.com/s2/favicons?domain=${slotProps.data.url}`"
                                    class="w-4 h-4"
                                />
                                <span class="font-medium break-all" :class="{ 'line-through opacity-60': slotProps.data.canceled }">{{ slotProps.data.url }}</span>
                                <Button
                                    icon="pi pi-external-link"
                                    text
                                    size="small"
                                    @click="() => openExternalSite(slotProps.data.url)"
                                />
                            </div>
                        </template>
                    </Column>

                    <Column field="type_name" :header="$t('Type')" style="min-width: 150px">
                        <template #body="slotProps">
                            <span class="font-medium text-surface-900">
                                {{ slotProps.data.type_name || '-' }}
                            </span>
                        </template>
                    </Column>

                        <Column field="link" :header="$t('Link')" style="min-width: 250px">
                            <template #body="slotProps">
                                <div v-if="slotProps.data.link" class="flex items-center gap-2">
                                    <span class="truncate max-w-xs" :data-tippy-content="slotProps.data.link">
                                        {{ slotProps.data.link }}
                                    </span>
                                    <Button
                                        icon="pi pi-external-link"
                                        text
                                        size="small"
                                        @click="() => openExternalSite(slotProps.data.link)"
                                    />
                                </div>
                                <span v-else class="text-surface-500">-</span>
                            </template>
                        </Column>

                        <Column field="sale" :header="$t('Value')" style="min-width: 140px">
                            <template #body="slotProps">
                                <span class="font-semibold text-primary-600">
                                    {{ formatItemPrice(slotProps.data.sale, slotProps.data.sale_coin) }}
                                </span>
                            </template>
                        </Column>

                        <Column field="link_status" :header="$t('Status')" style="min-width: 140px">
                            <template #body="slotProps">
                                <Tag
                                    v-if="slotProps.data.canceled"
                                    :value="$t('Canceled')"
                                    severity="danger"
                                />
                                <Tag
                                    v-else
                                    :value="slotProps.data.link_status === 'PRODUCTION' && slotProps.data.is_content_rejected ? $t('Content Rejected') : getStatusLabel(slotProps.data.link_status || 'PRODUCTION', $t)"
                                    :severity="slotProps.data.link_status === 'PRODUCTION' && slotProps.data.is_content_rejected ? 'danger' : getStatusSeverity(slotProps.data.link_status || 'PRODUCTION')"
                                />
                            </template>
                        </Column>

                        <Column field="observations" :header="$t('Observations')" style="min-width: 200px">
                            <template #body="slotProps">
                                <span v-if="slotProps.data.observations" class="text-sm text-surface-700">
                                    {{ slotProps.data.observations }}
                                </span>
                                <span v-else class="text-surface-500">-</span>
                            </template>
                        </Column>

                        <Column :header="$t('Article')" style="min-width: 180px">
                            <template #body="slotProps">
                                <div v-if="slotProps.data.auto_content === 1">
                                    <!-- Approved -->
                                    <div v-if="slotProps.data.client_approved_at" class="flex items-center gap-1 text-green-700">
                                        <i class="pi pi-check-circle"></i>
                                        <span class="text-xs font-medium">{{ $t('You approved this article') }}</span>
                                    </div>
                                    <!-- Rejected -->
                                    <div v-else-if="slotProps.data.client_rejected_at" class="flex items-center gap-1 text-red-700">
                                        <i class="pi pi-times-circle"></i>
                                        <span class="text-xs font-medium">{{ $t('Article rejected') }}</span>
                                    </div>
                                    <!-- Awaiting -->
                                    <div v-else-if="slotProps.data.sent_to_client_approval_at">
                                        <Button
                                            :label="$t('Evaluate Article')"
                                            icon="pi pi-pencil"
                                            severity="info"
                                            size="small"
                                            @click="openContentApprovalModal(slotProps.data)"
                                        />
                                    </div>
                                    <!-- Not yet sent -->
                                    <div v-else class="flex items-center gap-1 text-amber-600">
                                        <i class="pi pi-clock"></i>
                                        <span class="text-xs font-medium">{{ $t('Awaiting article submission') }}</span>
                                    </div>
                                </div>
                                <span v-else class="text-surface-400 text-xs">-</span>
                            </template>
                        </Column>

                        <Column :header="$t('Actions')" style="min-width: 180px">
                            <template #body="slotProps">
                                <div class="flex flex-col gap-2">
                                    <!-- Standard actions -->
                                    <div class="flex gap-2">
                                        <Button
                                            v-if="props.order.status === 'CANCELED' || slotProps.data.canceled"
                                            :label="$t('Canceled')"
                                            icon="pi pi-ban"
                                            severity="secondary"
                                            size="small"
                                            disabled
                                        />
                                        <Button
                                            v-else-if="!slotProps.data.is_content_confirmed"
                                            :label="slotProps.data.is_content_rejected ? $t('Revise Content') : (slotProps.data.content ? $t('Edit Content') : $t('Add Content'))"
                                            :icon="slotProps.data.is_content_rejected ? 'pi pi-exclamation-triangle' : (slotProps.data.content ? 'pi pi-pencil' : 'pi pi-plus')"
                                            :severity="slotProps.data.is_content_rejected ? 'danger' : (slotProps.data.content ? 'secondary' : 'primary')"
                                            size="small"
                                            :disabled="!slotProps.data.is_content_rejected && !!slotProps.data.sent_to_client_approval_at"
                                            @click="openContentModal(slotProps.data)"
                                        />
                                        <Button
                                            v-if="slotProps.data.is_content_confirmed && slotProps.data.content"
                                            :label="$t('View Content')"
                                            icon="pi pi-eye"
                                            severity="info"
                                            size="small"
                                            @click="openViewContentModal(slotProps.data)"
                                        />
                                        <Button
                                            v-if="slotProps.data.review_logs?.length"
                                            icon="pi pi-history"
                                            v-tooltip.top="$t('Review History')"
                                            severity="secondary"
                                            text
                                            size="small"
                                            @click="openHistoryDialog(slotProps.data)"
                                        />
                                    </div>
                                </div>
                            </template>
                        </Column>

                        <template #footer>
                            <div class="flex justify-end">
                                <div class="text-right space-y-1">
                                    <!-- Subtotal -->
                                    <div class="text-sm text-surface-600">
                                        <span class="font-medium">{{ $t('Subtotal') }}: </span>
                                        <span class="font-semibold">
                                            {{ Object.keys(orderSubtotalsByCurrency).length === 1 
                                                ? formatWithSymbol(Object.values(orderSubtotalsByCurrency)[0], Object.keys(orderSubtotalsByCurrency)[0])
                                                : Object.keys(orderSubtotalsByCurrency).map(currency => formatWithSymbol(orderSubtotalsByCurrency[currency], currency)).join(' + ')
                                            }}
                                        </span>
                                    </div>
                                    
                                    <!-- Payment Tax (if exists) -->
                                    <div v-if="Object.values(orderPaymentTaxByCurrency).some(tax => tax > 0)" class="text-sm text-surface-600">
                                        <span class="font-medium">{{ $t('Payment Processing Tax') }}: </span>
                                        <span class="font-semibold text-orange-600">
                                            {{ Object.keys(orderPaymentTaxByCurrency).length === 1 
                                                ? formatWithSymbol(Object.values(orderPaymentTaxByCurrency)[0], Object.keys(orderPaymentTaxByCurrency)[0])
                                                : Object.keys(orderPaymentTaxByCurrency).map(currency => formatWithSymbol(orderPaymentTaxByCurrency[currency], currency)).join(' + ')
                                            }}
                                        </span>
                                    </div>
                                    
                                    <!-- Total -->
                                    <div class="pt-1 border-t border-surface-200">
                                        <span class="text-sm font-medium">{{ $t('Total') }}: </span>
                                        <span class="text-lg font-bold text-primary-600">{{ formattedTotal }}</span>
                                    </div>
                                </div>
                            </div>
                        </template>
                    </DataTable>

                    <div v-if="orderItems.length === 0" class="text-center py-8 text-surface-500 italic">
                        {{ $t('No site have been added to this order yet.') }}
                    </div>
                </div>
            </template>
        </Card>

    <!-- Order Changes Section -->
    <Card v-if="changeLogs.length" class="mb-6">
        <template #header>
            <div class="p-4 sm:p-6 pb-0">
                <div class="flex items-center gap-3">
                    <i class="pi pi-history text-xl sm:text-2xl text-surface-500" />
                    <div>
                        <h3 class="text-lg font-semibold">{{ $t('Order changes') }}</h3>
                    </div>
                </div>
            </div>
        </template>

        <template #content>
            <ul class="space-y-3">
                <li
                    v-for="(log, index) in changeLogs"
                    :key="index"
                    class="flex items-start gap-3 pb-3 border-b border-surface-200 last:border-0 last:pb-0"
                >
                    <i
                        class="pi mt-0.5 flex-shrink-0"
                        :class="log.action === 'item_added' ? 'pi-plus-circle text-green-600' : 'pi-minus-circle text-red-600'"
                    ></i>
                    <div class="min-w-0 flex-1">
                        <div class="flex flex-wrap items-center gap-2">
                            <span
                                class="text-sm font-medium"
                                :class="log.action === 'item_added' ? 'text-green-700' : 'text-red-700'"
                            >
                                {{ log.action === 'item_added' ? $t('Item added') : $t('Item canceled') }}
                            </span>
                            <span v-if="log.site_url" class="text-sm text-surface-700 truncate">{{ log.site_url }}</span>
                            <span class="text-xs text-surface-400 ml-auto whitespace-nowrap">{{ formatDate(log.at) }}</span>
                        </div>
                        <p v-if="log.detail" class="text-xs text-surface-500 mt-1">{{ log.detail }}</p>
                    </div>
                </li>
            </ul>
        </template>
    </Card>

    <!-- Content Modal -->
<Dialog
    v-model:visible="showContentModal"
    :header="selectedOrderItem?.content ? $t('Edit Content') : $t('Add Content')"
    :modal="true"
    :closable="true"
    :style="{ width: '50vw' }"
    :breakpoints="{ '960px': '75vw', '640px': '95vw' }"
    @hide="closeContentModal"
>
    <div class="space-y-4">
        <div v-if="selectedOrderItem?.is_content_rejected && !selectedOrderItem?.is_content_revised" class="p-4 bg-red-50 border border-red-200 rounded-lg items-center">
            <h4 class="text-base font-semibold text-red-900 mb-3 flex items-center gap-2">
                <i class="pi pi-exclamation-triangle text-red-600"></i>
                {{ $t('Content Revision Required') }}
            </h4>
            <div v-if="selectedOrderItem?.rejection_reason" class="mt-3">
                <p class="text-sm font-medium text-red-900 mb-1">{{ $t('Feedback') }}:</p>
                <p class="text-red-800 text-sm bg-white p-2 rounded border border-red-200">
                    {{ selectedOrderItem.rejection_reason }}
                </p>
            </div>
        </div>
        <!-- Site Information Section -->
        <SiteFeaturesInfo v-if="siteFeatures" :features="siteFeatures" :accepted-anchors="selectedOrderItem?.accepted_anchors || 1" />
        
        <!-- Multiple Anchors Section -->
        <div>
            <label class="block text-sm font-medium text-surface-700 mb-3">
                {{ $t('Anchors') }} <span class="text-red-500">*</span>
                <span class="text-xs text-surface-500 ml-1">({{ $t('At least one is required') }})</span>
            </label>
            
            <div class="space-y-4">
                <div 
                    v-for="(anchor, index) in contentForm.anchors.slice(0, Math.min(visibleAnchors, selectedOrderItem?.accepted_anchors || 1))" 
                    :key="index"
                    class="p-4 border border-surface-200 rounded-lg bg-surface-50"
                >
                    <div class="flex items-center justify-between mb-3">
                        <h5 class="text-sm font-semibold text-surface-700">
                            {{ $t('Anchor') }} {{ index + 1 }}
                        </h5>
                        <button 
                            v-if="index > 0 && index === visibleAnchors - 1"
                            @click="removeAnchor(index)"
                            type="button"
                            class="text-red-500 hover:text-red-700 p-1 rounded"
                            :title="$t('Remove this anchor')"
                        >
                            <i class="pi pi-times text-sm"></i>
                        </button>
                    </div>
                    
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <!-- Anchor Name -->
                        <div>
                            <label :for="`anchor_name_${index}`" class="block text-xs font-medium text-surface-600 mb-1">
                                {{ $t('Anchor Name') }}
                            </label>
                            <InputText
                                :id="`anchor_name_${index}`"
                                v-model="anchor.name"
                                class="w-full"
                                :class="{ 'border-red-500': formErrors[`anchors.${index}.name`] }"
                                :placeholder="$t('Enter anchor text')"
                            />
                            <small v-if="formErrors[`anchors.${index}.name`]" class="text-red-500">
                                {{ formErrors[`anchors.${index}.name`][0] }}
                            </small>
                        </div>
                        
                        <!-- Anchor URL -->
                        <div>
                            <label :for="`anchor_url_${index}`" class="block text-xs font-medium text-surface-600 mb-1">
                                {{ $t('Anchor URL') }}
                            </label>
                            <InputText
                                :id="`anchor_url_${index}`"
                                v-model="anchor.url"
                                type="url"
                                class="w-full"
                                :class="{ 'border-red-500': formErrors[`anchors.${index}.url`] }"
                                :placeholder="$t('Enter target URL')"
                            />
                            <small v-if="formErrors[`anchors.${index}.url`]" class="text-red-500">
                                {{ formErrors[`anchors.${index}.url`][0] }}
                            </small>
                        </div>
                    </div>
                </div>
            </div>
            
            <!-- Add Anchor Buttons -->
            <div class="space-y-2 mt-3">
                <!-- Show "Add Anchor 2" button if we have less than 2 anchors visible and max anchors >= 2 -->
                <div 
                    v-if="visibleAnchors === 1 && (selectedOrderItem?.accepted_anchors || 1) >= 2"
                    class="flex justify-start"
                >
                    <button
                        @click="addAnchor(2)"
                        type="button"
                        class="flex items-center gap-2 px-4 py-3 text-sm text-gray-600 border-2 border-dashed border-gray-300 hover:border-gray-400 hover:text-gray-700 rounded-lg transition-colors duration-200 bg-transparent"
                    >
                        <i class="pi pi-plus text-sm"></i>
                        {{ $t('Add Anchor 2') }}
                    </button>
                </div>
                
                <!-- Show "Add Anchor 3" button if we have less than 3 anchors visible and max anchors >= 3 -->
                <div 
                    v-if="visibleAnchors <= 2 && (selectedOrderItem?.accepted_anchors || 1) >= 3"
                    class="flex justify-start"
                >
                    <button
                        @click="addAnchor(3)"
                        type="button"
                        class="flex items-center gap-2 px-4 py-3 text-sm text-gray-600 border-2 border-dashed border-gray-300 hover:border-gray-400 hover:text-gray-700 rounded-lg transition-colors duration-200 bg-transparent"
                    >
                        <i class="pi pi-plus text-sm"></i>
                        {{ $t('Add Anchor 3') }}
                    </button>
                </div>
            </div>
        </div>

        <!-- Language -->
        <div>
            <label for="language" class="block text-sm font-medium text-surface-700 mb-2">
                {{ $t('Language') }} <span class="text-red-500">*</span>
            </label>
            <Select
                id="language"
                v-model="contentForm.language"
                :options="formattedLanguages"
                optionLabel="name"
                optionValue="name"
                :placeholder="$t('Select language')"
                class="w-full"
                :invalid="!!formErrors.language"
                filter
                :filterPlaceholder="$t('Search language')"
            />
            <small v-if="formErrors.language" class="text-red-500">
                {{ Array.isArray(formErrors.language) ? formErrors.language[0] : formErrors.language }}
            </small>
        </div>

        <!-- Content Type Selection -->
        <div>
            <label class="block text-sm font-medium text-surface-700 mb-2">
                {{ $t('Content Type') }} <span class="text-red-500">*</span>
            </label>
            <div class="flex gap-4">
                <div class="flex items-center">
                    <RadioButton
                        v-model="contentType"
                        inputId="type_link"
                        name="content_type"
                        value="link"
                        @change="onContentTypeChange"
                    />
                    <label for="type_link" class="ml-2 cursor-pointer" @click.prevent="toggleContentType('link')">
                        {{ $t('Google Docs Link') }}
                    </label>
                </div>
                <div class="flex items-center">
                    <RadioButton
                        v-model="contentType"
                        inputId="type_article"
                        name="content_type"
                        value="article"
                        @change="onContentTypeChange"
                    />
                    <label for="type_article" class="ml-2 cursor-pointer" @click.prevent="toggleContentType('article')">
                        {{ $t('Request 500-word article') }}
                    </label>
                </div>
            </div>
        </div>

        <!-- Google Docs Link (only shown when Google Docs is selected) -->
        <div v-if="contentType === 'link'">
            <label for="content_link" class="block text-sm font-medium text-surface-700 mb-2">
                {{ $t('Google Docs Link') }} <span class="text-red-500">*</span>
            </label>
            <div class="space-y-3">
                <InputText
                    id="content_link"
                    v-model="contentForm.content_link"
                    class="w-full"
                    type="url"
                    :placeholder="$t('Enter Google Docs link (e.g., https://docs.google.com/document/d/...)')"
                    :invalid="!!formErrors.content_link || !!googleDocsValidationError"
                />
                
                <!-- Validation Error Message -->
                <div v-if="googleDocsValidationError" class="p-3 bg-red-50 border border-red-200 rounded-lg">
                    <div class="flex items-start gap-2">
                        <i class="pi pi-exclamation-triangle text-red-500 text-sm mt-0.5"></i>
                        <div>
                            <p class="text-red-800 text-sm font-medium mb-1">{{ $t('Validation Error') }}</p>
                            <p class="text-red-700 text-sm">{{ googleDocsValidationError }}</p>
                        </div>
                    </div>
                </div>
            </div>
            
            <small v-if="formErrors.content_link" class="text-red-500 block mt-1">
                {{ Array.isArray(formErrors.content_link) ? formErrors.content_link[0] : formErrors.content_link }}
            </small>
            <small class="text-surface-500 block mt-1">
                {{ $t('Paste the shareable link from Google Docs') }}
            </small>
        </div>
        <!-- Mensagem opcional para o admin -->
        <div class="space-y-2">
            <div v-if="selectedOrderItem?.is_content_rejected" class="p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
                <i class="pi pi-exclamation-triangle text-amber-600 mt-0.5"></i>
                <p class="text-amber-800 text-sm">{{ $t('Your previous submission was rejected. Use the field below to respond to the feedback.') }}</p>
            </div>
            <label class="block text-sm font-medium text-surface-700">
                {{ $t('Message to reviewer') }} <span class="text-surface-400 font-normal">({{ $t('optional') }})</span>
            </label>
            <Textarea
                v-model="contentForm.message"
                :placeholder="$t('Add a note for the reviewer...')"
                rows="3"
                class="w-full"
                :maxlength="2000"
            />
            <small class="text-surface-400">{{ (contentForm.message?.length || 0) }}/2000</small>
        </div>

        <!-- Mensagem explicativa para artigo de 500 palavras -->
        <div v-if="contentType === 'article'" class="p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div class="flex items-start gap-3">
                <i class="pi pi-info-circle text-blue-600 text-lg mt-0.5"></i>
                <div>
                    <h4 class="text-blue-900 font-medium mb-1">{{ $t('Article Generation Service') }}</h4>
                    <p class="text-blue-700 text-sm leading-relaxed">
                        {{ $t('Our team will generate an article of up to 500 words with the correct anchor URL and anchor name.') }}
                    </p>
                </div>
            </div>
        </div>
    </div>

    <template #footer>
        <div class="flex justify-end gap-2">
            <Button
                :label="$t('Cancel')"
                severity="secondary"
                outlined
                @click="closeContentModal"
                :disabled="submitting"
            />
            <Button
                :label="$t('Submit')"
                icon="pi pi-check"
                @click="() => submitContent()"
                :loading="submitting"
                :disabled="submitting"
            />
        </div>
    </template>
</Dialog>

<!-- View Content Modal (Read-only) -->
<Dialog
    v-model:visible="showViewContentModal"
    :header="$t('View Content')"
    :modal="true"
    :closable="true"
    :style="{ width: '50vw' }"
    :breakpoints="{ '960px': '75vw', '640px': '95vw' }"
    @hide="closeViewContentModal"
>
    <div v-if="selectedOrderItem?.content" class="space-y-6">
        <div v-if="selectedOrderItem?.is_content_rejected && !selectedOrderItem?.is_content_revised" class="p-4 bg-red-50 border border-red-200 rounded-lg items-center">
            <h4 class="text-base font-semibold text-red-900 mb-3 flex items-center gap-2">
                <i class="pi pi-exclamation-triangle text-red-600"></i>
                {{ $t('Content Revision Required') }}
            </h4>
            <div v-if="selectedOrderItem?.rejection_reason" class="mt-3">
                <p class="text-sm font-medium text-red-900 mb-1">{{ $t('Feedback') }}:</p>
                <p class="text-red-800 text-sm bg-white p-2 rounded border border-red-200">
                    {{ selectedOrderItem.rejection_reason }}
                </p>
            </div>
        </div>
        
        <!-- Site Information -->
        <SiteFeaturesInfo v-if="siteFeatures" :features="siteFeatures" :accepted-anchors="selectedOrderItem?.accepted_anchors || 1">
            <!-- Site URL -->
            <div class="flex items-center gap-2 pt-2 border-t border-surface-200">
                <i class="pi pi-external-link text-blue-600"></i>
                <span class="text-sm font-medium">{{ $t('Site URL') }}:</span>
                <a :href="selectedOrderItem.url" target="_blank" class="text-blue-600 hover:underline text-sm">
                    {{ selectedOrderItem.url }}
                </a>
            </div>
        </SiteFeaturesInfo>

        <!-- Content Details -->
        <div class="space-y-6">
            <h4 class="text-lg font-semibold text-surface-900 border-b border-surface-200 pb-2">
                {{ $t('Content Details') }}
            </h4>

            <!-- Anchors -->
            <div>
                <h5 class="text-sm font-medium text-surface-700 mb-3">{{ $t('Anchors') }}</h5>
                <div class="space-y-3">
                    <!-- Anchor 1 -->
                    <div v-if="selectedOrderItem.content.anchor_name || selectedOrderItem.content.anchor_url" class="p-3 border border-surface-200 rounded-lg bg-surface-50">
                        <div class="flex items-center gap-2 mb-2">
                            <i class="pi pi-link text-primary-600 text-sm"></i>
                            <span class="text-sm font-semibold text-surface-700">{{ $t('Anchor') }} 1</span>
                        </div>
                        <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div>
                                <span class="text-xs font-medium text-surface-600">{{ $t('Name') }}:</span>
                                <p class="text-sm text-surface-900">{{ selectedOrderItem.content.anchor_name || '-' }}</p>
                            </div>
                            <div>
                                <span class="text-xs font-medium text-surface-600">{{ $t('URL') }}:</span>
                                <div v-if="selectedOrderItem.content.anchor_url">
                                    <a 
                                        :href="selectedOrderItem.content.anchor_url" 
                                        target="_blank" 
                                        class="text-blue-600 hover:underline break-all text-sm"
                                    >
                                        {{ selectedOrderItem.content.anchor_url }}
                                    </a>
                                </div>
                                <p v-else class="text-sm text-surface-500">-</p>
                            </div>
                        </div>
                    </div>

                    <!-- Anchor 2 -->
                    <div v-if="selectedOrderItem.content.anchor_name_2 || selectedOrderItem.content.anchor_url_2" class="p-3 border border-surface-200 rounded-lg bg-surface-50">
                        <div class="flex items-center gap-2 mb-2">
                            <i class="pi pi-link text-primary-600 text-sm"></i>
                            <span class="text-sm font-semibold text-surface-700">{{ $t('Anchor') }} 2</span>
                        </div>
                        <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div>
                                <span class="text-xs font-medium text-surface-600">{{ $t('Name') }}:</span>
                                <p class="text-sm text-surface-900">{{ selectedOrderItem.content.anchor_name_2 || '-' }}</p>
                            </div>
                            <div>
                                <span class="text-xs font-medium text-surface-600">{{ $t('URL') }}:</span>
                                <div v-if="selectedOrderItem.content.anchor_url_2">
                                    <a 
                                        :href="selectedOrderItem.content.anchor_url_2" 
                                        target="_blank" 
                                        class="text-blue-600 hover:underline break-all text-sm"
                                    >
                                        {{ selectedOrderItem.content.anchor_url_2 }}
                                    </a>
                                </div>
                                <p v-else class="text-sm text-surface-500">-</p>
                            </div>
                        </div>
                    </div>

                    <!-- Anchor 3 -->
                    <div v-if="selectedOrderItem.content.anchor_name_3 || selectedOrderItem.content.anchor_url_3" class="p-3 border border-surface-200 rounded-lg bg-surface-50">
                        <div class="flex items-center gap-2 mb-2">
                            <i class="pi pi-link text-primary-600 text-sm"></i>
                            <span class="text-sm font-semibold text-surface-700">{{ $t('Anchor') }} 3</span>
                        </div>
                        <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div>
                                <span class="text-xs font-medium text-surface-600">{{ $t('Name') }}:</span>
                                <p class="text-sm text-surface-900">{{ selectedOrderItem.content.anchor_name_3 || '-' }}</p>
                            </div>
                            <div>
                                <span class="text-xs font-medium text-surface-600">{{ $t('URL') }}:</span>
                                <div v-if="selectedOrderItem.content.anchor_url_3">
                                    <a 
                                        :href="selectedOrderItem.content.anchor_url_3" 
                                        target="_blank" 
                                        class="text-blue-600 hover:underline break-all text-sm"
                                    >
                                        {{ selectedOrderItem.content.anchor_url_3 }}
                                    </a>
                                </div>
                                <p v-else class="text-sm text-surface-500">-</p>
                            </div>
                        </div>
                    </div>

                    <!-- Show message if no anchors -->
                    <div v-if="!selectedOrderItem.content.anchor_name && !selectedOrderItem.content.anchor_url && 
                                !selectedOrderItem.content.anchor_name_2 && !selectedOrderItem.content.anchor_url_2 &&
                                !selectedOrderItem.content.anchor_name_3 && !selectedOrderItem.content.anchor_url_3" class="text-surface-500">-</div>
                </div>
            </div>

            <!-- Language -->
            <div>
                <h5 class="text-sm font-medium text-surface-700 mb-2">{{ $t('Language') }}</h5>
                <p class="text-surface-900">{{ selectedOrderItem.content.language || '-' }}</p>
            </div>

            <!-- Content Link -->
            <div>
                <h5 class="text-sm font-medium text-surface-700 mb-2">
                    {{ selectedOrderItem.content.auto_content === 1 ? $t('Google Docs Link') : $t('Google Docs Link') }}
                </h5>
                <div v-if="selectedOrderItem.content.auto_content === 1" class="space-y-2">
                    <div v-if="selectedOrderItem.content.content_link" class="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                        <div class="flex items-start gap-2 mb-2">
                            <i class="pi pi-user-check text-blue-600 mt-0.5"></i>
                            <span class="text-blue-800 font-medium text-sm">{{ $t('Article provided by Linkarme') }}</span>
                        </div>
                        <a 
                            :href="selectedOrderItem.content.content_link" 
                            target="_blank" 
                            class="text-blue-600 hover:underline break-all flex items-center gap-2 text-sm"
                        >
                            <i class="pi pi-external-link"></i>
                            {{ selectedOrderItem.content.content_link }}
                        </a>
                    </div>
                </div>
                <div v-else-if="selectedOrderItem.content.content_link">
                    <div class="p-3 bg-green-50 border border-green-200 rounded-lg">
                        <div class="flex items-start gap-2 mb-2">
                            <i class="pi pi-user text-green-600 mt-0.5"></i>
                            <span class="text-green-800 font-medium text-sm">{{ $t('Link provided by client') }}</span>
                        </div>
                        <a 
                            :href="selectedOrderItem.content.content_link" 
                            target="_blank" 
                            class="text-green-600 hover:underline break-all flex items-center gap-2 text-sm"
                        >
                            <i class="pi pi-external-link"></i>
                            {{ selectedOrderItem.content.content_link }}
                        </a>
                    </div>
                </div>
                <p v-else class="text-surface-500">-</p>
            </div>
        </div>
    </div>

    <template #footer>
        <div class="flex justify-end">
            <Button
                :label="$t('Close')"
                severity="secondary"
                @click="closeViewContentModal"
            />
        </div>
    </template>
</Dialog>

<!-- Wise Transfer Details Modal -->
<Dialog
    v-model:visible="showWiseModal"
    modal
    :header="$t('Wise transfer details')"
    :style="{ width: '560px' }"
>
    <WiseTransferInstructions
        v-if="wiseBankDetails"
        :order-number="order.number"
        :bank-details="{ ...wiseBankDetails, reference: order.number }"
        :total="order.total_amount"
        :currency="order.order_items?.[0]?.sale_coin ?? 'USD'"
        :order-id="order.id"
    />
</Dialog>

<!-- Mercado Pago Payment Details Modal -->
<Dialog
    v-model:visible="showMpModal"
    modal
    :header="order.payment_method === 'pix' ? $t('Pix payment details') : $t('Boleto payment details')"
    :style="{ width: '600px' }"
>
    <PixInstructions
        v-if="order.payment_method === 'pix' && mpPaymentDetails"
        :order-number="order.number"
        :qr-code-base64="mpPaymentDetails.qr_code_base64"
        :qr-code-text="mpPaymentDetails.qr_code_text"
        :expiration-date="mpPaymentDetails.expiration_date"
        :total="order.total_amount"
        currency="BRL"
        :order-id="order.id"
    />
    <BoletoInstructions
        v-if="order.payment_method === 'boleto' && mpPaymentDetails"
        :order-number="order.number"
        :boleto-url="mpPaymentDetails.boleto_url"
        :barcode="mpPaymentDetails.barcode"
        :expiration-date="mpPaymentDetails.expiration_date"
        :total="order.total_amount"
        currency="BRL"
        :order-id="order.id"
    />
</Dialog>

<ClientContentApprovalModal
    v-model:visible="showContentApprovalModal"
    :item="contentApprovalItem"
    @reject="(contentId) => { showContentApprovalModal = false; rejectingContentId = contentId; showRejectApprovalModal = true; }"
/>

<ClientRejectContentModal
    v-model:visible="showRejectApprovalModal"
    :content-id="rejectingContentId"
/>

<ReviewHistoryDialog
    :visible="showHistoryDialog"
    :item="historyItem"
    @hide="closeHistoryDialog"
/>
</div>
</template>

<style scoped>
/* Order items table: header titles must always stay on a single line */
.order-items-table :deep(.p-datatable-column-title) {
    white-space: nowrap;
}

.order-items-table :deep(.p-datatable-header-cell) {
    white-space: nowrap;
}

/* Stepper Container */
.stepper-container {
    width: 100%;
    padding: 0;
}

.stepper-list {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    position: relative;
    padding: 0;
}

/* Stepper Item */
.stepper-item {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    position: relative;
    text-align: center;
}

/* Stepper Header */
.stepper-header {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.75rem;
    position: relative;
    z-index: 2;
}

/* Stepper Number (Circle) */
.stepper-number {
    width: 3rem;
    height: 3rem;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 700;
    font-size: 1.125rem;
    transition: all 0.3s ease;
    background: #f3f4f6;
    color: #9ca3af;
    border: 3px solid #e5e7eb;
}

/* Stepper Details Container */
.stepper-details {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.25rem;
}

/* Stepper Title */
.stepper-title {
    font-size: 0.875rem;
    font-weight: 600;
    color: #6b7280;
    transition: all 0.3s ease;
    max-width: 150px;
    word-wrap: break-word;
}

/* Stepper Date */
.stepper-date {
    font-size: 0.75rem;
    color: #6b7280;
    display: flex;
    align-items: center;
    gap: 0.25rem;
    transition: all 0.3s ease;
    margin-top: 0.5rem;
}

.stepper-date-pending {
    color: #9ca3af;
}

/* Stepper Separator (Line) */
.stepper-separator {
    position: absolute;
    top: 1.5rem;
    left: calc(50% + 1.5rem);
    width: calc(100% - 3rem);
    height: 3px;
    background: #e5e7eb;
    z-index: 1;
    transition: all 0.3s ease;
}

/* Active State */
.stepper-item.active .stepper-number {
    background: linear-gradient(135deg, #3b82f6, #2563eb);
    color: white;
    border-color: #60a5fa;
    box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.1);
}

.stepper-item.active .stepper-title {
    color: #1d4ed8;
    font-weight: 700;
}

.stepper-item.active .stepper-date {
    color: #3b82f6;
    font-weight: 600;
}

.stepper-item.active .stepper-separator {
    background: linear-gradient(to right, #3b82f6, #e5e7eb);
}

/* Partial State (Blue - at least one item reached but not all completed) */
.stepper-item.partial .stepper-number {
    background: linear-gradient(135deg, #3b82f6, #2563eb);
    color: white;
    border-color: #60a5fa;
    box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.1);
}

.stepper-item.partial .stepper-title {
    color: #1d4ed8;
    font-weight: 700;
}

.stepper-item.partial .stepper-date {
    color: #3b82f6;
    font-weight: 600;
}

.stepper-item.partial .stepper-separator {
    background: linear-gradient(to right, #3b82f6, #e5e7eb);
}

/* Completed State */
.stepper-item.completed .stepper-number {
    background: linear-gradient(135deg, #10b981, #059669);
    color: white;
    border-color: #10b981;
    box-shadow: 0 2px 8px rgba(16, 185, 129, 0.2);
}

.stepper-item.completed .stepper-title {
    color: #059669;
}

.stepper-item.completed .stepper-date {
    color: #059669;
    font-weight: 600;
}

.stepper-item.completed .stepper-separator {
    background: #10b981;
}

/* Cancelled State (Red) */
.stepper-item.cancelled .stepper-number {
    background: linear-gradient(135deg, #ef4444, #dc2626);
    color: white;
    border-color: #ef4444;
    box-shadow: 0 2px 8px rgba(239, 68, 68, 0.2);
}

.stepper-item.cancelled .stepper-title {
    color: #dc2626;
    font-weight: 700;
}

.stepper-item.cancelled .stepper-date {
    color: #dc2626;
    font-weight: 600;
}

.stepper-item.cancelled .stepper-separator {
    background: #ef4444;
}

/* Dark Mode */
:global(.dark) .stepper-number {
    background: #1f2937;
    color: #6b7280;
    border-color: #374151;
}

:global(.dark) .stepper-title {
    color: #9ca3af;
}

:global(.dark) .stepper-date {
    color: #9ca3af;
}

:global(.dark) .stepper-separator {
    background: #374151;
}

:global(.dark) .stepper-item.active .stepper-number {
    background: linear-gradient(135deg, #3b82f6, #2563eb);
    color: white;
    border-color: #60a5fa;
}

:global(.dark) .stepper-item.active .stepper-title {
    color: #60a5fa;
}

:global(.dark) .stepper-item.active .stepper-date {
    color: #60a5fa;
}

:global(.dark) .stepper-item.partial .stepper-number {
    background: linear-gradient(135deg, #3b82f6, #2563eb);
    color: white;
    border-color: #60a5fa;
}

:global(.dark) .stepper-item.partial .stepper-title {
    color: #60a5fa;
}

:global(.dark) .stepper-item.partial .stepper-date {
    color: #60a5fa;
}

:global(.dark) .stepper-item.partial .stepper-separator {
    background: linear-gradient(to right, #3b82f6, #374151);
}

:global(.dark) .stepper-item.completed .stepper-number {
    background: linear-gradient(135deg, #10b981, #059669);
    color: white;
}

:global(.dark) .stepper-item.completed .stepper-title {
    color: #34d399;
}

:global(.dark) .stepper-item.completed .stepper-date {
    color: #34d399;
}

:global(.dark) .stepper-item.completed .stepper-separator {
    background: #10b981;
}

:global(.dark) .stepper-item.cancelled .stepper-number {
    background: linear-gradient(135deg, #ef4444, #dc2626);
    color: white;
}

:global(.dark) .stepper-item.cancelled .stepper-title {
    color: #f87171;
}

:global(.dark) .stepper-item.cancelled .stepper-date {
    color: #f87171;
}

:global(.dark) .stepper-item.cancelled .stepper-separator {
    background: #ef4444;
}

/* Responsive Design */
@media (max-width: 1024px) {
    .stepper-number {
        width: 2.5rem;
        height: 2.5rem;
        font-size: 1rem;
    }

    .stepper-title {
        font-size: 0.75rem;
        max-width: 120px;
    }

    .stepper-date {
        font-size: 0.65rem;
    }

    .stepper-separator {
        top: 1.25rem;
        left: calc(50% + 1.25rem);
        width: calc(100% - 2.5rem);
    }
}

@media (max-width: 768px) {
    .stepper-list {
        flex-direction: column;
        gap: 1.5rem;
    }

    .stepper-item {
        flex-direction: row;
        text-align: left;
        width: 100%;
    }

    .stepper-header {
        flex-direction: row;
        align-items: center;
        gap: 1rem;
    }

    .stepper-details {
        align-items: flex-start;
    }

    .stepper-title {
        max-width: none;
        text-align: left;
    }

    .stepper-separator {
        display: none;
    }

    .stepper-number {
        flex-shrink: 0;
    }
}

@media (max-width: 640px) {
    .stepper-number {
        width: 2.25rem;
        height: 2.25rem;
        font-size: 0.875rem;
    }

    .stepper-title {
        font-size: 0.75rem;
    }

    .stepper-date {
        font-size: 0.625rem;
    }
}
</style>
